import { supabase } from '../lib/supabase';
import { BlogPost, BlogPostFormData, BlogCategory, BlogTag } from '../types/blog';

export const blogService = {
  async getPosts(filters?: { status?: string; category?: string; limit?: number; offset?: number }) {
    let query = supabase
      .from('admin_blog_posts_with_details')
      .select('*')
      .order('created_at', { ascending: false });

    if (filters?.status) {
      query = query.eq('status', filters.status);
    }

    if (filters?.category) {
      query = query.eq('category_slug', filters.category);
    }

    if (filters?.limit) {
      query = query.limit(filters.limit);
    }

    if (filters?.offset) {
      query = query.range(filters.offset, filters.offset + (filters.limit || 10) - 1);
    }

    const { data, error } = await query;

    if (error) throw error;
    return data as BlogPost[];
  },

  async getPublishedPosts(filters?: { category?: string; tag?: string; limit?: number; offset?: number }) {
    let query = supabase
      .from('published_blog_posts_with_details')
      .select('*')
      .order('published_at', { ascending: false });

    if (filters?.category) {
      query = query.eq('category_slug', filters.category);
    }

    if (filters?.limit) {
      query = query.limit(filters.limit);
    }

    if (filters?.offset) {
      query = query.range(filters.offset, filters.offset + (filters.limit || 10) - 1);
    }

    const { data, error } = await query;

    if (error) throw error;
    return data as BlogPost[];
  },

  async getPostBySlug(slug: string, isAdmin = false) {
    const view = isAdmin ? 'admin_blog_posts_with_details' : 'published_blog_posts_with_details';
    const { data, error } = await supabase
      .from(view)
      .select('*')
      .eq('slug', slug)
      .maybeSingle();

    if (error) throw error;
    return data as BlogPost | null;
  },

  async getPostById(id: string) {
    const { data, error } = await supabase
      .from('admin_blog_posts_with_details')
      .select('*')
      .eq('id', id)
      .maybeSingle();

    if (error) throw error;
    return data as BlogPost | null;
  },

  async createPost(postData: BlogPostFormData) {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('Not authenticated');

    const { tag_ids, ...postFields } = postData;

    const cleanedFields = {
      ...postFields,
      category_id: postFields.category_id || null,
      featured_image_url: postFields.featured_image_url || null,
      seo_title: postFields.seo_title || null,
      seo_description: postFields.seo_description || null,
      seo_keywords: postFields.seo_keywords && postFields.seo_keywords.length > 0 ? postFields.seo_keywords : null,
      published_at: postFields.published_at || (postFields.status === 'published' ? new Date().toISOString() : null),
      author_id: user.id,
      reading_time_minutes: this.calculateReadingTime(postData.content)
    };

    const { data: post, error: postError } = await supabase
      .from('blog_posts')
      .insert(cleanedFields)
      .select()
      .single();

    if (postError) throw postError;

    if (tag_ids && tag_ids.length > 0) {
      const tagInserts = tag_ids.map(tag_id => ({
        post_id: post.id,
        tag_id
      }));

      const { error: tagError } = await supabase
        .from('blog_post_tags')
        .insert(tagInserts);

      if (tagError) throw tagError;
    }

    return post;
  },

  async updatePost(id: string, postData: Partial<BlogPostFormData>) {
    const { tag_ids, ...postFields } = postData;

    if (postFields.content) {
      postFields.reading_time_minutes = this.calculateReadingTime(postFields.content);
    }

    const cleanedFields: any = {
      ...postFields,
      updated_at: new Date().toISOString()
    };

    if (postFields.status === 'published' && postFields.published_at === undefined) {
      const { data: existingPost } = await supabase
        .from('blog_posts')
        .select('published_at')
        .eq('id', id)
        .single();

      if (!existingPost?.published_at) {
        cleanedFields.published_at = new Date().toISOString();
      }
    }

    if (postFields.category_id !== undefined) {
      cleanedFields.category_id = postFields.category_id || null;
    }
    if (postFields.featured_image_url !== undefined) {
      cleanedFields.featured_image_url = postFields.featured_image_url || null;
    }
    if (postFields.seo_title !== undefined) {
      cleanedFields.seo_title = postFields.seo_title || null;
    }
    if (postFields.seo_description !== undefined) {
      cleanedFields.seo_description = postFields.seo_description || null;
    }
    if (postFields.seo_keywords !== undefined) {
      cleanedFields.seo_keywords = postFields.seo_keywords && postFields.seo_keywords.length > 0 ? postFields.seo_keywords : null;
    }
    if (postFields.published_at !== undefined) {
      cleanedFields.published_at = postFields.published_at || null;
    }

    const { error: postError } = await supabase
      .from('blog_posts')
      .update(cleanedFields)
      .eq('id', id);

    if (postError) throw postError;

    if (tag_ids !== undefined) {
      await supabase
        .from('blog_post_tags')
        .delete()
        .eq('post_id', id);

      if (tag_ids.length > 0) {
        const tagInserts = tag_ids.map(tag_id => ({
          post_id: id,
          tag_id
        }));

        const { error: tagError } = await supabase
          .from('blog_post_tags')
          .insert(tagInserts);

        if (tagError) throw tagError;
      }
    }

    return true;
  },

  async deletePost(id: string) {
    const { error } = await supabase
      .from('blog_posts')
      .delete()
      .eq('id', id);

    if (error) throw error;
    return true;
  },

  async incrementViewCount(postId: string) {
    const { error } = await supabase.rpc('increment_blog_post_view_count', {
      p_post_id: postId
    });

    if (error) console.error('Error incrementing view count:', error);
  },

  async getCategories() {
    const { data, error } = await supabase
      .from('blog_categories')
      .select('*')
      .order('name');

    if (error) throw error;
    return data as BlogCategory[];
  },

  async createCategory(name: string, description?: string) {
    const slug = this.generateSlug(name);

    const { data, error } = await supabase
      .from('blog_categories')
      .insert({ name, slug, description })
      .select()
      .single();

    if (error) throw error;
    return data as BlogCategory;
  },

  async updateCategory(id: string, name: string, description?: string) {
    const slug = this.generateSlug(name);

    const { error } = await supabase
      .from('blog_categories')
      .update({ name, slug, description, updated_at: new Date().toISOString() })
      .eq('id', id);

    if (error) throw error;
    return true;
  },

  async deleteCategory(id: string) {
    const { error } = await supabase
      .from('blog_categories')
      .delete()
      .eq('id', id);

    if (error) throw error;
    return true;
  },

  async getTags() {
    const { data, error } = await supabase
      .from('blog_tags')
      .select('*')
      .order('name');

    if (error) throw error;
    return data as BlogTag[];
  },

  async createTag(name: string) {
    const slug = this.generateSlug(name);

    const { data, error } = await supabase
      .from('blog_tags')
      .insert({ name, slug })
      .select()
      .single();

    if (error) throw error;
    return data as BlogTag;
  },

  async updateTag(id: string, name: string) {
    const slug = this.generateSlug(name);

    const { error } = await supabase
      .from('blog_tags')
      .update({ name, slug, updated_at: new Date().toISOString() })
      .eq('id', id);

    if (error) throw error;
    return true;
  },

  async deleteTag(id: string) {
    const { error } = await supabase
      .from('blog_tags')
      .delete()
      .eq('id', id);

    if (error) throw error;
    return true;
  },

  generateSlug(text: string): string {
    return text
      .toLowerCase()
      .replace(/[^a-z0-9\s-]/g, '')
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-')
      .trim();
  },

  calculateReadingTime(content: string): number {
    const strippedContent = content.replace(/<[^>]*>/g, '');
    const wordCount = strippedContent.split(/\s+/).filter(word => word.length > 0).length;
    const wordsPerMinute = 200;
    return Math.max(1, Math.ceil(wordCount / wordsPerMinute));
  }
};
